import {
  Cartesian3,
  Cartographic,
  EllipsoidTerrainProvider,
  Math,
  sampleTerrainMostDetailed,
} from "@cesium/engine";
import {
  DitapAlert,
  Geocoder,
  GeocoderViewModel,
  PointEntity,
  Utils,
} from "@cesium/widgets";

/**
 * 주소와 랜드마크를 찾아 해당 위치로 카메라 이동시키는 위젯. 지오코딩은 Vworld Api를 사용하여 수행.</br>
 * DitapViewer 내부적으로 생성되어 사용하는 위젯.</br>
 * A widget for finding addresses and landmarks, and flying the camera to them.
 * Geocoding is performed using Vworld Api.</br>
 * The widget used internally by DitapViewer.
 * @extends Geocoder
 *
 * @alias DitapGeocoder
 * @constructor
 *
 * @param {object} object 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
 * @param {Element|string} object.container 위젯을 포함할 DOM 요소 또는 ID.</br> The DOM element or ID that will contain the widget.
 * @param {Scene} object.scene 사용할 Scene 인스턴스.</br> The Scene instance to use.
 * @param {DitapVworldGeocoderService} object.geocoderServices 사용할 지오코더 서비스.</br> The geocoder services to be used.
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Search Address' 참조
 * // 1. DitapViewer 생성 시 DitapGeocoder 생성 가능.
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *    geocoder: {
 *      key: "Vworld에서 발급받은 Api Key"
 *    },
 * });
 *
 * // 2. 단독으로 선언하여 활성화
 * const viewer = new Ditap.DitapViewer("ditapContainer");
 *
 * const geocoderContainer = viewer.container.getElementsByClassName(
 *    "js-ditap-toolbal-top-wrap"
 * )[0];
 *
 * const koreanGeocoderOptions = {
 *    key: "vworld 발급 api key",
 * };
 *
 * const geocoderService = new Ditap.DitapVworldGeocoderService(koreanGeocoderOptions);
 *
 * const geocoderOptions = {
 *    scene: viewer.scene,
 *    container: geocoderContainer,
 *    geocoderService: geocoderService,
 * };
 *
 * const ditapGeocoder = new Ditap.DitapGeocoder(geocoderOptions, viewer);
 */
class DitapGeocoder extends Geocoder {
  constructor(options, viewer) {
    super(options);

    const container = options.container;
    const mobileContainer = viewer.mobileBtnContainer;
    const viewModel = new GeocoderViewModel(options);

    super.destroy();
    // container.getElementsByTagName("form")[0].remove();
    this.isMobile = Utils.isBodyWidthLessThan600();

    const div1 = document.createElement("div");
    div1.classList.add("ditap-search");

    const div2 = document.createElement("div");
    const label = document.createElement("label");
    const input1 = document.createElement("input");
    div2.classList.add("ditap-search-address");
    div2.classList.add("js-ditap-search-address");
    input1.classList.add("ditap-search-inp", "ditap-search-inp-address");
    input1.classList.add("js-ditap-search-text");
    input1.setAttribute("type", "text");
    input1.setAttribute("placeholder", "주소를 입력해주세요");
    input1.setAttribute("name", "address");
    label.appendChild(input1);
    label.setAttribute("for", "address");
    div2.appendChild(label);

    const label2 = document.createElement("label");
    const input2 = document.createElement("input");
    input2.classList.add("btn-reset");
    input2.classList.add("ditap-search-find");
    input2.classList.add("js-ditap-search-btn");
    input2.setAttribute("type", "button");
    input2.setAttribute("name", "search");
    label2.appendChild(input2);
    label2.setAttribute("for", "search");
    div2.appendChild(label2);

    // 검색 결과 목록 ul에 append
    const resultWrap = document.createElement("div");
    resultWrap.classList.add("ditap-search-list-wrap");
    const ul = document.createElement("ul");
    ul.classList.add("ditap-search-list");
    ul.classList.add("js-ditap-search-list");
    ul.classList.add("hide");
    resultWrap.appendChild(ul);
    div2.appendChild(resultWrap);

    const div3 = document.createElement("div");
    const label3 = document.createElement("label");
    const input3 = document.createElement("input");
    div3.classList.add("ditap-search-coordinate");
    div3.classList.add("js-ditap-search-coordinate");
    div3.classList.add("hide");
    input3.classList.add("ditap-search-inp");
    input3.classList.add("ditap-latitude-inp");
    input3.classList.add("js-ditap-latitude-value");
    input3.setAttribute("type", "text");
    input3.setAttribute("placeholder", "위도");
    input3.setAttribute("name", "latitude");
    input3.setAttribute("id", "latitude");
    label3.setAttribute("for", "latitude");
    label3.appendChild(input3);
    const latWrap = document.createElement("div");
    latWrap.classList.add("ditap-coordinate-wrap", "ditap-latitude-wrap");
    latWrap.appendChild(label3);
    div3.appendChild(latWrap);

    const label4 = document.createElement("label");
    const input4 = document.createElement("input");
    input4.classList.add("ditap-search-inp");
    input4.classList.add("ditap-longitude-inp");
    input4.classList.add("js-ditap-longitude-value");
    input4.setAttribute("type", "text");
    input4.setAttribute("name", "longitude");
    input4.setAttribute("placeholder", "경도");
    label4.setAttribute("for", "longitude");
    label4.appendChild(input4);
    div3.appendChild(label4);
    const longWrap = document.createElement("div");
    longWrap.classList.add("ditap-coordinate-wrap", "ditap-longitude-wrap");
    longWrap.appendChild(label4);
    div3.appendChild(longWrap);

    const div5 = document.createElement("div");
    div5.classList.add("ditap-search-label");

    const label5 = document.createElement("label");
    const input5 = document.createElement("input");
    input5.classList.add("btn-reset");
    input5.classList.add("ditap-search-find");
    input5.setAttribute("type", "button");
    input5.setAttribute("name", "search");
    input5.setAttribute("id", "search");
    label5.setAttribute("for", "search");
    label5.appendChild(input5);
    div5.appendChild(label5);
    div3.appendChild(div5);

    const div4 = document.createElement("div");
    const button1 = document.createElement("button");
    const button2 = document.createElement("button");
    div4.classList.add("ditap-search-btn-wrap");
    button1.textContent = "주소";
    button1.classList.add("ditap-search-btn");
    button1.classList.add("js-ditap-search-address-btn");
    button1.classList.add("gold");
    button1.classList.add("js-ditap-search-select-btn"); // 선택된 버튼(gold)
    button2.textContent = "좌표";
    button2.classList.add("ditap-search-btn");
    button2.classList.add("js-ditap-search-coordinate-btn");
    div4.appendChild(button1);
    div4.appendChild(button2);

    const closeWrap = document.createElement("div");
    closeWrap.classList.add("ditap-popup-close-wrap");
    const closeBtn = document.createElement("button");
    closeBtn.classList.add("ditap-popup-close-btn", "ditap-geocoder-close-btn");
    closeWrap.appendChild(closeBtn);

    if (this.isMobile) {
      div1.appendChild(div4);
      div1.appendChild(closeWrap);
      div1.appendChild(div2);
      div1.appendChild(div3);
      viewer.container.querySelector(".cesium-viewer").prepend(div1);
      div1.classList.add("ditap-mobile-search");
    } else {
      div1.appendChild(div2);
      div1.appendChild(div3);
      div1.appendChild(div4);
      div1.appendChild(closeWrap);
      container.prepend(div1);
      closeBtn.classList.add("hide");
    }

    // 모바일 UI
    const li = document.createElement("li");
    li.classList.add("ditap-func-btn-item");
    const button = document.createElement("button");
    button.classList.add("ditap-mobile-btn", "ditap-mobile-search-btn");
    const imgButton = document.createElement("p");
    const text2 = document.createElement("p");
    imgButton.classList.add("ditap-function-btn-img");
    text2.classList.add("ditap-btn-tooltip");

    text2.textContent = "검색";
    button.appendChild(imgButton);
    button.appendChild(text2);
    li.appendChild(button);
    // mobileContainer.appendChild(li);
    mobileContainer.querySelector(".js-position-search").appendChild(li);

    this.isOpen = false;

    button.addEventListener(
      "click",
      (this._toolBtnEvent = () => {
        const callback = () => {
          viewer.clickToolbar(this);
          viewer.mobileBtnWrapper?.classList.add("hide");
        };
        viewer.offAllInterations(callback);
      })
    );

    closeBtn.addEventListener(
      "click",
      (this._closeBtnEvent = () => {
        this.isOpen = false;
        this._searchContainer.style.display = "none";
        this.addressInput.value = "";
        this.latitudeInput.value = "";
        this.longitudeInput.value = "";
        this.searchList.classList.add("hide");
        this.searchList.innerHTML = "";
        viewer.mobileBtnWrapper?.classList.remove("hide");
      })
    );

    // 이벤트 바인딩에 필요한 멤버변수
    this._searchContainer = div1;
    this._addressContainer = div2;
    this._coordinateContainer = div3;
    this._btnContainer = div4;
    this._addressButton = button1;
    this._coordinateButton = button2;
    this._addressInput = input1;
    this._addressSearchButton = input2;
    this._latitudeInput = input3;
    this._longitudeInput = input4;
    this._coordinateSearchButton = input5;
    this._searchList = ul;
    this._mobileBtn = button;
    this._imgButton = imgButton;
    this._closeBtn = closeBtn;
    this._viewModel = viewModel;
    this._viewer = viewer;

    this.createEvent(options, viewer, viewModel);
    this.createResizeEvent(viewer, container);
  }

  /*
   * 윈도우 크기 조절 시 이벤트 생성.</br>
   * Create an event for window resize.
   */
  createResizeEvent(viewer, container) {
    const searchContainer = this._searchContainer;
    const addressContainer = this._addressContainer;
    const coordinateContainer = this._coordinateContainer;
    const btnContainer = this._btnContainer;

    const resizeOriginal = () => {
      // 윈도우의 현재 너비를 확인
      const currentIsMobile = Utils.isBodyWidthLessThan600();

      if (this.isMobile !== currentIsMobile) {
        if (currentIsMobile) {
          this.isMobile = true;
          if (!this.isOpen) {
            searchContainer.style.display = "none";
          } else {
            searchContainer.style.display = "block";
          }
          searchContainer.classList.add("ditap-mobile-search");
          this._closeBtn.classList.remove("hide");
          addressContainer.parentNode.insertBefore(
            btnContainer,
            addressContainer
          );
          viewer.container
            .querySelector(".cesium-viewer")
            .appendChild(searchContainer);
        } else {
          if (
            searchContainer.style.display === "block" ||
            searchContainer.style.display === "none"
          ) {
            searchContainer.style.display = "flex";
          }
          btnContainer.parentNode.insertBefore(
            coordinateContainer,
            btnContainer
          );
          coordinateContainer.parentNode.insertBefore(
            addressContainer,
            coordinateContainer
          );

          container.prepend(searchContainer);
          searchContainer.classList.remove("ditap-mobile-search");
          this._closeBtn.classList.add("hide");
          this.isMobile = false;
        }
      }
    };
    this._resizeEvent = () => {
      resizeOriginal();
      // 창너비 측정이 안됬을 경우를 대비해 재시도
      setTimeout(resizeOriginal, 500);
    };
    window.addEventListener("resize", this._resizeEvent);
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    this._viewer = viewer;
  }
  get viewer() {
    return this._viewer;
  }

  /*
   * 주소/좌표 검색을 위한 컨테이너 요소.</br>
   * The container element for address/coordinate retrieval.
   * @type {Element}
   */
  set searchContainer(searchContainer) {
    this._searchContainer = searchContainer;
  }
  get searchContainer() {
    return this._searchContainer;
  }

  /*
   * 주소 검색 버튼 요소.</br>
   * Address search button element.
   * @type {Element}
   */
  set addressButton(addressButton) {
    this._addressButton = addressButton;
  }
  get addressButton() {
    return this._addressButton;
  }

  /*
   * 좌표 검색 버튼 요소.</br>
   * Coordinate search button element.
   * @type {Element}
   */
  set coordinateButton(coordinateButton) {
    this._coordinateButton = coordinateButton;
  }
  get coordinateButton() {
    return this._coordinateButton;
  }

  /*
   * 주소 검색 입력 폼 요소.</br>
   * The Address search input form element.
   * @type {Element}
   */
  set addressInput(addressInput) {
    this._addressInput = addressInput;
  }
  get addressInput() {
    return this._addressInput;
  }

  /*
   * 위도 입력 폼 요소.</br>
   * The latitude input form element.
   * @type {Element}
   */
  set latitudeInput(latitudeInput) {
    this._latitudeInput = latitudeInput;
  }
  get latitudeInput() {
    return this._latitudeInput;
  }

  /*
   * 경도 입력 폼 요소.</br>
   * The longitude input form element.
   * @type {Element}
   */
  set longitudeInput(longitudeInput) {
    this._longitudeInput = longitudeInput;
  }
  get longitudeInput() {
    return this._longitudeInput;
  }

  /*
   * 주소/좌표 검색 결과 목록 요소.</br>
   * The address/coordinate search result list element.
   * @type {Element}
   */
  set searchList(searchList) {
    this._searchList = searchList;
  }
  get searchList() {
    return this._searchList;
  }

  /*
   * 주소 검색 버튼 요소.</br>
   * The address search button element.
   * @type {Element}
   */
  set addressSearchButton(addressSearchButton) {
    this._addressSearchButton = addressSearchButton;
  }
  get addressSearchButton() {
    return this._addressSearchButton;
  }

  /*
   * 좌표 검색 버튼 요소.</br>
   * The coordinate search button element.
   * @type {Element}
   */
  set coordinateSearchButton(coordinateSearchButton) {
    this._coordinateSearchButton = coordinateSearchButton;
  }
  get coordinateSearchButton() {
    return this._coordinateSearchButton;
  }

  /*
   * 주소 클릭 시 실행 할 이벤트.</br>
   * The events that run when the address button is clicked.
   * @type {Function}
   */
  set addressButtonEvent(addressButtonEvent) {
    this._addressButtonEvent = addressButtonEvent;
  }
  get addressButtonEvent() {
    return this._addressButtonEvent;
  }

  /*
   * 좌표 클릭 시 실행 할 이벤트.</br>
   * The events that run when the coordinate button is clicked.
   * @type {Function}
   */
  set coordinateButtonEvent(coordinateButtonEvent) {
    this._coordinateButtonEvent = coordinateButtonEvent;
  }
  get coordinateButtonEvent() {
    return this._coordinateButtonEvent;
  }

  /*
   * Window 키 이벤트를 누를 때 실행 할 이벤트.</br>
   * The events to run when a key event is pressed in the Windows environment.
   * @type {Function}
   */
  set windowKeyDownEvent(windowKeyDownEvent) {
    this._windowKeyDownEvent = windowKeyDownEvent;
  }
  get windowKeyDownEvent() {
    return this._windowKeyDownEvent;
  }

  /*
   * 주소 검색 버튼 클릭 시 실행 할 이벤트.</br>
   * The events that run when the address search button is clicked.
   * @type {Function}
   */
  set addressSearchEvent(addressSearchEvent) {
    this._addressSearchEvent = addressSearchEvent;
  }
  get addressSearchEvent() {
    return this._addressSearchEvent;
  }

  /*
   * 좌표 검색 버튼 클릭 시 실행 할 이벤트.</br>
   * The events that run when the coordinate search button is clicked.
   * @type {Function}
   */
  set coordinateSearchEvent(coordinateSearchEvent) {
    this._coordinateSearchEvent = coordinateSearchEvent;
  }
  get coordinateSearchEvent() {
    return this._coordinateSearchEvent;
  }

  /*
   * 검색 결과 목록을 스크롤할 때 실행 할 이벤트.</br>
   * The events that run when scrolling in the search results list.
   * @type {Function}
   */
  set listScrollEvent(listScrollEvent) {
    this._listScrollEvent = listScrollEvent;
  }
  get listScrollEvent() {
    return this._listScrollEvent;
  }

  /*
   * 주소 입력 폼 클릭 시 실행할 이벤트.</br>
   * The events that run when the address input form is clicked.
   * @type {Function}
   */
  set addressInputEvent(addressInputEvent) {
    this._addressInputEvent = addressInputEvent;
  }
  get addressInputEvent() {
    return this._addressInputEvent;
  }

  /*
   * 위도 입력 폼 클릭 시 실행할 이벤트.</br>
   * The events that run when the latitude input form is clicked.
   * @type {Function}
   */
  set latitudeInputEvent(latitudeInputEvent) {
    this._latitudeInputEvent = latitudeInputEvent;
  }
  get latitudeInputEvent() {
    return this._latitudeInputEvent;
  }

  /*
   * 경도 입력 폼 클릭 시 실행할 이벤트.</br>
   * The events that run when the longitude input form is clicked.
   * @type {Function}
   */
  set longitudeInputEvent(longitudeInputEvent) {
    this._longitudeInputEvent = longitudeInputEvent;
  }
  get longitudeInputEvent() {
    return this._longitudeInputEvent;
  }

  /*
   * Window에서 클릭 이벤트가 발생할 때 실행할 이벤트.</br>
   * The events that run when a click event occurs in a Windows environment.
   * @type {Function}
   */
  set windowClickEvent(windowClickEvent) {
    this._windowClickEvent = windowClickEvent;
  }
  get windowClickEvent() {
    return this._windowClickEvent;
  }

  /*
   * 주소 검색 결과가 존재하지 않을 때 표시되는 DitapAlert.</br>
   * The DitapAlert displayed when there are no address search results available.
   * @type {DitapAlert}
   */
  set searchAddressAlert(searchAddressAlert) {
    if (
      searchAddressAlert instanceof DitapAlert ||
      searchAddressAlert === undefined
    ) {
      this._searchAddressAlert = searchAddressAlert;
    }
  }
  get searchAddressAlert() {
    return this._searchAddressAlert;
  }

  /*
   * 좌표 검색 결과가 존재하지 않을 때 표시되는 DitapAlert.</br>
   * The DitapAlert displayed when there are no coordinate search results available.
   * @type {DitapAlert}
   */
  set searchCoordinateAlert(searchCoordinateAlert) {
    if (
      searchCoordinateAlert instanceof DitapAlert ||
      searchCoordinateAlert === undefined
    ) {
      this._searchCoordinateAlert = searchCoordinateAlert;
    }
  }
  get searchCoordinateAlert() {
    return this._searchCoordinateAlert;
  }

  /*
   * 유효하지 않은 키를 사용할 경우 표시되는 DitapAlert.</br>
   * The DitapAlert displayed when an invalid key is used.
   * @type {DitapAlert}
   */
  set invalidKeyAlert(invalidKeyAlert) {
    if (
      invalidKeyAlert instanceof DitapAlert ||
      invalidKeyAlert === undefined
    ) {
      this._invalidKeyAlert = invalidKeyAlert;
    }
  }
  get invalidKeyAlert() {
    return this._invalidKeyAlert;
  }

  /*
   * 키가 만료된 경우 표시되는 DitapAlert.<br>
   * Notification that appears when the key has expired.
   * @type {DitapAlert}
   */
  set expireKeyAlert(expireKeyAlert) {
    if (expireKeyAlert instanceof DitapAlert || expireKeyAlert === undefined) {
      this._expireKeyAlert = expireKeyAlert;
    }
  }
  get expireKeyAlert() {
    return this._expireKeyAlert;
  }

  toggle() {
    const searchContainer = this._searchContainer;
    if (this.isOpen) {
      searchContainer.style.display = "none"; // 숨김으로 변경
      this._imgButton.classList.remove("on");
      this.isOpen = false;
    } else {
      searchContainer.style.display = "block"; // 보이게 변경
      this._imgButton.classList.add("on");
      this.isOpen = true;
    }
  }

  /*
   * 이벤트 생성
   */
  createEvent(object, viewer, viewModel) {
    // "주소" 버튼 클릭
    this.addressButton.addEventListener(
      "click",
      (this.addressButtonEvent = () => {
        this.showAddressSearchForm();
      })
    );

    // "좌표" 버튼 클릭
    this.coordinateButton.addEventListener(
      "click",
      (this.coordinateButtonEvent = () => {
        this.showCoordinateSearchForm();
      })
    );

    // 주소 검색란 클릭
    // this.addressInput.addEventListener(
    //   "click",
    //   (this.clickAddressInput = () => {
    //     this.showSearchList(viewer);
    //   })
    // );

    // 결과 목록 스크롤 이벤트 - 최하단에 닿았을 때 목록 더 불러오기
    this.searchList.addEventListener(
      "scroll",
      (this.listScrollEvent = () => {
        this.appendResultList(object.geocoderService, viewer, viewModel);
      })
    );

    // "돋보기" 버튼 클릭 > 주소 검색
    this.addressSearchButton.addEventListener(
      "click",
      (this.addressSearchEvent = () => {
        this.searchAddressButton(object.geocoderService, viewer, viewModel);
      })
    );

    // "돋보기" 버튼 클릭 > 좌표 검색
    this.coordinateSearchButton.addEventListener(
      "click",
      (this.coordinateSearchEvent = () => {
        this.searchCoordinateButton(viewer, viewModel);
      })
    );

    // 엔터 클릭 시 주소/좌표 검색 실행
    window.addEventListener(
      "keydown",
      (this.windowKeyDownEvent = (event) => {
        this.onKeyDown(object.geocoderService, viewer, viewModel, event);
      })
    );

    // 위도 입력 폼 정규화(소수점)
    this.latitudeInput.addEventListener(
      "input",
      (this.latitudeInputEvent = (event) => {
        const result = this.checkRegularExpression(event.target.value);
        if (!result) {
          this.latitudeInput.value = event.target.value.slice(0, -1);
        }
      })
    );

    // 경도 입력 폼 정규화(소수점)
    this.longitudeInput.addEventListener(
      "input",
      (this.longitudeInputEvent = (event) => {
        const result = this.checkRegularExpression(event.target.value);
        if (!result) {
          this.longitudeInput.value = event.target.value.slice(0, -1);
        }
      })
    );
  }

  /*
   * 위도, 경도가 올바르게 입력되었는지 판단.</br>
   * Determine if the value entered as a parameter is a real number.
   * @param {string} value 실수 여부를 판단할 값.</br> Value to determine if it is an real number.
   * @returns {boolean} 매개변수가 실수인 경우 true, 그렇지 않으면 false 반환.</br> Returns true if the parameter is a real number, otherwise returns false.
   */
  checkRegularExpression(value) {
    const pattern = /^[+-]?\d*(\.?\d*)$/;
    if (pattern.test(value)) {
      return true;
    }
    return false;
  }

  /**
   * 위젯 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the widget.  Should be called if permanently removing the widget from layout.
   */
  destroy() {
    const viewer = this._viewer;
    // 이벤트 제거
    this.addressButton.removeEventListener("click", this.addressButtonEvent);
    if (this.coordinateButtonEvent) {
      this.coordinateButton.removeEventListener(
        "click",
        this.coordinateButtonEvent
      );
      this.coordinateButtonEvent = undefined;
    }
    if (this.coordinateSearchEvent) {
      this.coordinateSearchButton.removeEventListener(
        "click",
        this.coordinateSearchEvent
      );
      this.coordinateSearchEvent = undefined;
    }
    if (this.addressSearchEvent) {
      this.addressSearchButton.removeEventListener(
        "click",
        this.addressSearchEvent
      );
      this.addressSearchEvent = undefined;
    }

    this.searchList.removeEventListener("scroll", this.listScrollEvent);
    this.addressInput.removeEventListener("click", this.addressInputEvent);
    this.latitudeInput.addEventListener("input", this.latitudeInputEvent);
    this.longitudeInput.addEventListener("input", this.longitudeInputEvent);
    window.removeEventListener("keydown", this.windowKeyDownEvent);
    if (this.windowClickEvent) {
      viewer.container.removeEventListener("click", this.windowClickEvent);
      this.windowClickEvent = undefined;
    }
    // 요소 제거
    this.searchContainer.remove();

    if (this._resizeEvent) {
      window.removeEventListener("resize", this._resizeEvent);
      this._resizeEvent = undefined;
    }
    if (this._toolBtnEvent) {
      this._mobileBtn.removeEventListener("click", this._toolBtnEvent);
      this._toolBtnEvent = undefined;
    }
    if (this._closeBtnEvent) {
      this._closeBtn.removeEventListener("click", this._closeBtnEvent);
      this._closeBtnEvent = undefined;
    }
    this._viewModel.destroy();
    this._mobileBtn = undefined;
    this._toolBtnEvent = undefined;

    // 멤버 변수 초기화
    this.addressButton = undefined;
    this.addressButtonEvent = undefined;
    this.addressInput = undefined;
    this.addressInputEvent = undefined;
    this.addressSearchButton = undefined;
    this.coordinateButton = undefined;
    this.coordinateSearchButton = undefined;
    this.invalidKeyAlert = undefined;
    this.latitudeInput = undefined;
    this.latitudeInputEvent = undefined;
    this.longitudeInput = undefined;
    this.longitudeInputEvent = undefined;
    this.listScrollEvent = undefined;
    this.searchAddressAlert = undefined;
    this.searchCoordinateAlert = undefined;
    this.windowClickEvent = undefined;
    this.windowKeyDownEvent = undefined;
    this.searchContainer = undefined;
    this.searchList = undefined;
    this.viewer = undefined;
    this.isOpen = undefined;
    this._addressContainer = undefined;
    this._btnContainer = undefined;
    this._closeBtn = undefined;
    this._container = undefined;
    this._coordinateContainer = undefined;
    this._imgButton = undefined;
    this._onInputBegin = undefined;
    this._onInputEnd = undefined;
    this._onTextBoxFocus = undefined;
    this._searchSuggestionsContainer = undefined;
    this._textBox = undefined;
    this._viewModel = undefined;
    this._form = undefined;
    this.isDestroyed = undefined;
    this.isMobile = undefined;
  }

  // /**
  //  * 위젯 파괴 여부 반환.</br>
  //  * Returns the presence or absence of widget destroy.
  //  * @return {boolean} 객체가 파괴 된 경우 true, 그렇지 않으면 false 반환.</br> true if the object has been destroyed, false otherwise.
  //  */
  // isDestroyed() {
  //   return super.isDestroyed();
  // }

  /*
   * 주소 검색 폼 표시.</br>
   * Show address search form.
   */
  showAddressSearchForm() {
    if (!this.addressButton.classList.contains("gold")) {
      this.addressButton.classList.add("gold");
      this.addressButton.classList.add("js-ditap-search-select-btn");
      this.coordinateButton.classList.remove("gold");
      this.coordinateButton.classList.remove("js-ditap-search-select-btn");

      this.searchContainer
        .getElementsByClassName("js-ditap-search-address")[0]
        .classList.remove("hide");
      this.searchList.classList.add("hide");
      this.searchContainer
        .getElementsByClassName("js-ditap-search-coordinate")[0]
        .classList.add("hide");

      this.addressInput.value = "";
    }
  }

  /*
   * 좌표 검색 폼 표시.</br>
   * Show coordinate search form.
   */
  showCoordinateSearchForm() {
    if (!this.coordinateButton.classList.contains("gold")) {
      this.searchContainer
        .getElementsByClassName("js-ditap-search-coordinate")[0]
        .classList.remove("hide");
      this.searchContainer
        .getElementsByClassName("js-ditap-search-address")[0]
        .classList.add("hide");

      this.coordinateButton.classList.add("gold");
      this.coordinateButton.classList.add("js-ditap-search-select-btn");
      this.addressButton.classList.remove("gold");
      this.addressButton.classList.remove("js-ditap-search-select-btn");

      this.addressInput.classList.remove("on");

      this.latitudeInput.value = "";
      this.longitudeInput.value = "";
    }
  }

  /*
   * Vworld Api 통해 얻은 검색 결과 표시.</br>
   * Execute functions based on search results obtained through Vworld Api.
   * @param {DitapVworldGeocoderService} geocoderService 사용할 지오코더 서비스.</br> The geocoder services to be used.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {GeocoderViewModel} viewModel 사용할 View Model.</br> View model to use.
   */
  searchAddressButton(geocoderService, viewer, viewModel) {
    const that = this;
    const query = this.addressInput.value;

    const result = geocoderService.geocode(query);
    result.then((resource) => {
      if (resource === "INVALID_KEY") {
        if (!that.invalidKeyAlert) {
          // 유효하지 않은 인증 Key인 경우 Alert
          const invalidKeyAlertObj = {
            type: "warning",
            title: "좌표 검색",
            content1: "등록되지 않은 인증키입니다.",
            content2: "인증키를 발급 받은 후에 시도하세요.",
            buttons: "ONLYOK",
            open: false,
            callback: (alert) => {
              that.invalidKeyAlert = undefined;
              alert.destroy();
            },
          };
          that.invalidKeyAlert = new DitapAlert(invalidKeyAlertObj);
        }
        that.invalidKeyAlert.open();
      } else if (resource === "EXPIRE_KEY") {
        if (!that.expireKeyAlert) {
          // 유효하지 않은 인증 Key인 경우 Alert
          const expireKeyAlertObj = {
            type: "warning",
            title: "좌표 검색",
            content1: "만료된 인증키입니다.",
            content2: "인증키를 재발급 받은 후에 시도하세요.",
            buttons: "ONLYOK",
            open: false,
            callback: (alert) => {
              that.expireKeyAlert = undefined;
              alert.destroy();
            },
          };
          that.expireKeyAlert = new DitapAlert(expireKeyAlertObj);
        }
        that.expireKeyAlert.open();
      } else if (resource.length > 0) {
        const parameter = {
          results: resource,
          viewer: viewer,
          viewModel: viewModel,
        };
        that.showAddressResult(parameter);
      } else {
        if (!that.searchAddressAlert) {
          // 주소 검색 결과가 없을 때 Alert
          const noResultAddressObj = {
            type: "warning",
            title: "주소 검색",
            content1: "검색 결과가 없습니다.",
            content2: "올바른 주소를 다시 입력해주세요.",
            buttons: "ONLYOK",
            open: false,
            callback: (alert) => {
              that.searchAddressAlert = undefined;
              alert.destroy();
            },
          };
          that.searchAddressAlert = new DitapAlert(noResultAddressObj);
        }
        that.addressInput.value = "";
        that.searchAddressAlert.open();
        that.searchList.classList.add("hide");
        that.searchList.replaceChildren();
      }
    });
  }

  /*
   * 올바른 위도(-90~90), 경도(-180~180)값 확인 후 해당 위치로 카메라 이동.</br>
   * Ensure correct latitude (-90~90), longitude (-180~180) values and move the camera to that location.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {GeocoderViewModel} viewModel 사용할 View Model.</br> View model to use.
   */
  searchCoordinateButton(viewer, viewModel) {
    const latitude = Number(
      this.searchContainer.getElementsByClassName("js-ditap-latitude-value")[0]
        .value
    );
    const longitude = Number(
      this.searchContainer.getElementsByClassName("js-ditap-longitude-value")[0]
        .value
    );

    if (
      latitude &&
      longitude &&
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180
    ) {
      const cartesian = new Cartesian3.fromDegrees(longitude, latitude);
      this.activateSuggestion(viewer, viewModel, cartesian);
    } else {
      if (!this.searchCoordinateAlert) {
        // 좌표 검색 결과가 없을 때 Alert
        const noResultCoordinateObj = {
          type: "warning",
          title: "좌표 검색",
          content1: "검색 결과가 없습니다.",
          content2:
            "올바른 좌표를 다시 입력해주세요. \n위도(-90~90), 경도(-180, 180)",
          buttons: "ONLYOK",
          open: false,
          callback: (alert) => {
            this.searchCoordinateAlert = undefined;
            alert.destroy();
          },
        };
        this.searchCoordinateAlert = new DitapAlert(noResultCoordinateObj);
      }
      this.latitudeInput.value = "";
      this.longitudeInput.value = "";
      this.searchCoordinateAlert.open();
    }
  }

  /*
   * Vworld Api 통해 얻은 결과 표출.</br>
   * Outputs results from Vworld Api to the list.
   * @param {object} parameter 다음과 같은 속성을 가진 객체.</br> Object with the following properties:
   * @param {Promise.<Array.<GeocoderService.Result>>} parameter.results Vwolrd Api에서 얻은 결과.</br> results from Vworld Api.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {GeocoderViewModel} viewModel 사용할 View Model.</br> View model to use.
   * @param {boolean} [parameter.isAppend] 목록 추가 여부.</br> Whether to add to the list.
   */
  showAddressResult(parameter) {
    const results = parameter.results;

    this.searchList.classList.remove("hide");
    if (!parameter.isAppend) {
      this.searchList.replaceChildren();
    }
    this.addressInput.classList.add("on");

    for (let i = 0; i < results.length; i++) {
      const li = document.createElement("button");
      li.classList.add(
        "ditap-search-item",
        "js-ditap-search-item",
        "ditap-custom-button"
      );
      const p = document.createElement("p");
      p.classList.add("ditap-search-item-mark");
      const div = document.createElement("div");
      div.classList.add("ditap-search-item-title-wrap");
      const p2 = document.createElement("p");
      p2.classList.add("ditap-search-item-title");
      p2.textContent = results[i].displayName;
      const p3 = document.createElement("p");
      p3.classList.add("ditap-search-item-content");
      p3.textContent = results[i].road;

      const input = document.createElement("input");
      input.setAttribute("type", "hidden");
      input.value = results[i].destination;

      div.appendChild(p2);
      div.appendChild(p3);
      li.appendChild(p);
      li.appendChild(div);
      li.append(input);
      this.searchList.appendChild(li);

      li.addEventListener("click", () => {
        parameter.viewer.container.removeEventListener(
          "click",
          this.windowClickEvent
        );

        this.addressInput.classList.remove("on");
        this.activateSuggestion(
          parameter.viewer,
          parameter.viewModel,
          results[i].destination
        );

        let displayName = results[i].displayName;
        if (results[i].displayName) {
          displayName = results[i].road;
        }

        this.addressInput.value = displayName;
        this.searchList.classList.add("hide");
      });
    }

    // 목록 보여질 때 검색 결과 외 다른 부분 클릭 시 검색 결과 초기화
    parameter.viewer.container.addEventListener(
      "click",
      (this.windowClickEvent = (event) => {
        this.resetSearchResult(event);
      }),
      { once: true }
    );
  }

  /*
   * 검색 결과 목록 표출.</br>
   * Show search results list.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   */
  showSearchList(viewer) {
    // 검색 결과 있는 경우 목록 보여주기
    if (this.addressInput.value !== "") {
      this.addressInput.classList.add("on");
      // this.searchList.classList.remove("hide");
      // 목록 보여질 때 검색 결과 외 다른 부분 클릭 시 검색 결과 초기화
      viewer.container.addEventListener("click", (event) => {
        this.resetSearchResult(event);
      });
    }
  }

  /*
   * 주소/좌표 검색 활성화.</br>
   * Enable address search or coordinate search.
   * @param {DitapVworldGeocoderService} geocoderService 사용할 지오코더 서비스.</br> The geocoder services to be used.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {GeocoderViewModel} viewModel 사용할 View Model.</br> View model to use.
   * @param {Event} event 키보드 이벤트.</br> Keyboard event.
   */
  onKeyDown(geocoderService, viewer, viewModel, event) {
    if (event.key === "Enter") {
      // 주소 검색
      if (this.addressButton.classList.contains("gold")) {
        if (this.addressInput === document.activeElement) {
          const that = this;
          const query = this.addressInput.value;

          const result = geocoderService.geocode(query);
          result.then((resource) => {
            if (resource === "INVALID_KEY") {
              if (!that.invalidKeyAlert) {
                // 유효하지 않은 인증 Key인 경우 Alert
                const invalidKeyAlertObj = {
                  type: "warning",
                  title: "좌표 검색",
                  content1: "등록되지 않은 인증키입니다.",
                  content2: "인증키를 발급 받은 후에 시도하세요.",
                  buttons: "ONLYOK",
                  open: false,
                  callback: (alert) => {
                    that.invalidKeyAlert = undefined;
                    alert.destroy();
                  },
                };
                that.invalidKeyAlert = new DitapAlert(invalidKeyAlertObj);
              }
              that.invalidKeyAlert.open();
            } else if (resource === "EXPIRE_KEY") {
              if (!that.expireKeyAlert) {
                // 유효하지 않은 인증 Key인 경우 Alert
                const expireKeyAlertObj = {
                  type: "warning",
                  title: "좌표 검색",
                  content1: "만료된 인증키입니다.",
                  content2: "인증키를 재발급 받은 후에 시도하세요.",
                  buttons: "ONLYOK",
                  open: false,
                  callback: (alert) => {
                    that.expireKeyAlert = undefined;
                    alert.destroy();
                  },
                };
                that.expireKeyAlert = new DitapAlert(expireKeyAlertObj);
              }
              that.expireKeyAlert.open();
            } else if (resource.length > 0) {
              const parameter = {
                results: resource,
                viewer: viewer,
                viewModel: viewModel,
              };
              that.showAddressResult(parameter);
            } else {
              if (!that.searchAddressAlert) {
                // 주소 검색 결과가 없을 때 Alert
                const noResultAddressObj = {
                  type: "warning",
                  title: "주소 검색",
                  content1: "검색 결과가 없습니다.",
                  content2: "올바른 주소를 다시 입력해주세요.",
                  buttons: "ONLYOK",
                  open: false,
                  callback: (alert) => {
                    that.searchAddressAlert = undefined;
                    alert.destroy();
                  },
                };
                that.searchAddressAlert = new DitapAlert(noResultAddressObj);
              }
              this.addressInput.value = "";
              that.searchAddressAlert.open();
              that.searchList.classList.add("hide");
              that.searchList.replaceChildren();
            }
          });
        }
      } else if (
        this.coordinateButton.classList.contains("js-ditap-search-select-btn")
      ) {
        // 좌표 검색
        // 위도 or 경도 input 활성화 되어있을 때만 실행
        if (
          this._latitudeInput === document.activeElement ||
          this._longitudeInput === document.activeElement
        ) {
          this.searchCoordinateButton(viewer, viewModel);
        }
      }
    }
  }

  /*
   * 다음 페이지 결과 목록에 추가.</br>
   * Get the results of the next page and add them to the list.
   * @param {DitapVworldGeocoderService} geocoderService 사용할 지오코더 서비스.</br> The geocoder services to be used.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {GeocoderViewModel} viewModel 사용할 View Model.</br> View model to use.
   */
  appendResultList(geocoderService, viewer, viewModel) {
    const list = this.searchList;
    if (list.scrollHeight - list.scrollTop === list.clientHeight) {
      const query = this.addressInput.value;
      const that = this;

      const result = geocoderService.geocode(query, true);
      result.then((resource) => {
        if (resource) {
          if (resource.length > 0) {
            const parameter = {
              results: resource,
              viewer: viewer,
              viewModel: viewModel,
              isAppend: true,
            };
            that.showAddressResult(parameter);
          }
        }
      });
    }
  }

  /*
   * 검색 결과 초기화.</br>
   * Initialize search results.
   * @param {Event} event 키보드 이벤트.</br> Keyboard event.
   */
  resetSearchResult(event) {
    if (
      !this.searchContainer.contains(event.target) &&
      event.target !== this.searchList &&
      this.addressInput.classList.contains("on")
    ) {
      this.addressInput.classList.remove("on");
      this.searchList.classList.add("hide");
    }
  }

  /*
   * 선택 대상으로 카메라 시점 이동 및 마커 생성.</br>
   * Moves the camera point of view to the selected destination and generates a marker.
   * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
   * @param {GeocoderViewModel} viewModel 사용할 View Model.</br> View model to use.
   * @param {Cartesian3} destination 시점 이동할 목적지.</br> Destination to move the point of view.
   */
  async activateSuggestion(viewer, viewModel, destination) {
    const cartographic = Cartographic.fromCartesian(destination);
    if (isNaN(cartographic.height)) {
      cartographic.height = 0;
    }

    destination = Cartographic.toCartesian(
      cartographic,
      viewer.scene.globe.ellipsoid
    );
    viewModel.destinationFound(viewModel, destination);

    let newPosition;
    let isEllipsoid = false;
    if (viewer.terrainProvider instanceof EllipsoidTerrainProvider) {
      isEllipsoid = true;
    }

    if (!isEllipsoid) {
      //터레인 있을때
      const cartographic = Cartographic.fromCartesian(destination);

      const longitude = Math.toDegrees(cartographic.longitude);
      const latitude = Math.toDegrees(cartographic.latitude);

      const position = Cartographic.fromDegrees(longitude, latitude);

      newPosition = await sampleTerrainMostDetailed(viewer.terrainProvider, [
        position,
      ]).then(function (updatedPositions) {
        cartographic.height = updatedPositions[0].height;
        return Cartographic.toCartesian(
          cartographic,
          viewer.scene.globe.ellipsoid
        );
      });
    } else {
      newPosition = destination;
    }

    // 해당 위치에 마커 생성(pointEntity)
    const pointEntity = new PointEntity(viewer);
    viewer.ditapStorage.pointEntity[pointEntity.id] = pointEntity;

    const point = Utils.createPoint({
      viewer: viewer,
      position: newPosition,
    });

    pointEntity.pointEntity = point;
    pointEntity.setBillboard("flag");
  }
}

export default DitapGeocoder;
